import { datadogRum } from "@datadog/browser-rum";
import { User } from "@/shared/interfaces/user/User.interface";
import { CONSENTS, hasCookieConsentById } from "@/shared/scripts/ConsentManagementService";

let isInitialized = false;
let pendingViewName: string | null = null;

function canInit(): boolean {
    return Boolean(
        process.env.VUE_APP_DD_APPLICATION_ID
        && process.env.VUE_APP_DD_CLIENT_TOKEN
    );
}

function hasConsent(): boolean {
    return hasCookieConsentById(CONSENTS.DATADOG_RUM);
}

function doInit(): void {
    if (isInitialized) {
        return;
    }

    datadogRum.init({
        applicationId          : process.env.VUE_APP_DD_APPLICATION_ID as string,
        clientToken            : process.env.VUE_APP_DD_CLIENT_TOKEN as string,
        site                   : (process.env.VUE_APP_DD_SITE as string) || "datadoghq.com",
        service                : "iwb-encoding-funnel",
        env                    : process.env.VUE_APP_ENVIRONMENT_NAME || "local",
        version                : process.env.npm_package_version || "dev",
        sessionSampleRate      : 40,
        sessionReplaySampleRate: 20,
        trackUserInteractions  : true,
        trackResources         : true,
        trackLongTasks         : true,
        trackViewsManually     : true,
        defaultPrivacyLevel    : "mask-user-input"
        // Distributed tracing disabled until the backend APIs (classifieds / payment /
        // customers management) allow the x-datadog-* and traceparent/tracestate headers
        // in their CORS Access-Control-Allow-Headers. With allowedTracingUrls set, tracked
        // sessions inject those headers into cross-origin API calls, triggering a CORS
        // preflight the backends reject — which breaks all API communication.
        // allowedTracingUrls     : [
        //     process.env.VUE_APP_CLASSIFIEDS_MANAGEMENT_URL,
        //     process.env.VUE_APP_PRICING_URL,
        //     process.env.VUE_APP_CUSTOMERS_MANAGEMENT_URL
        // ].filter(Boolean) as string[]
    });

    isInitialized = true;

    // With trackViewsManually, the session records nothing until startView() runs.
    // Init is consent-gated, so the router's initial startView call usually fires
    // before init and no-ops. Flush that pending view now so the session is recorded.
    if (pendingViewName !== null) {
        datadogRum.startView({ name: pendingViewName });
        pendingViewName = null;
    }
}

/**
 * Initialize Datadog RUM if credentials are configured and user consent is granted.
 * If consent is not yet granted, defers init until the Usercentrics CMP fires an event.
 */
export function initDatadog(): void {
    if (!canInit()) {
        return;
    }

    if (hasConsent()) {
        doInit();
        return;
    }

    // Usercentrics dispatches UC_UI_CMP_EVENT on consent updates. Re-check on change.
    const onConsentChange = (): void => {
        if (hasConsent()) {
            doInit();
            window.removeEventListener("UC_UI_CMP_EVENT", onConsentChange);
        }
    };
    window.addEventListener("UC_UI_CMP_EVENT", onConsentChange);
}

export function setDatadogUser(user: User | null): void {
    if (!isInitialized || !user || user.isAnonymous) {
        return;
    }
    datadogRum.setUser({
        id    : user.id,
        email : user.email,
        name  : user.username
    });
}

export function clearDatadogUser(): void {
    if (!isInitialized) {
        return;
    }
    datadogRum.clearUser();
}

export function datadogAddError(error: unknown, context?: Record<string, unknown>): void {
    if (!isInitialized) {
        return;
    }
    datadogRum.addError(error, context);
}

export function datadogStartView(name: string): void {
    if (!isInitialized) {
        // Init may be deferred until consent; remember the latest requested view
        // so doInit() can start it once the SDK is initialized.
        pendingViewName = name;
        return;
    }
    datadogRum.startView({ name: name });
}
